import React from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import ErrorBoundary from "./ErrorBoundary";
import "./index.css";
import "leaflet/dist/leaflet.css";
import { AuthProvider } from "./lib/auth-store";
// CAPACITOR REMOVED: AppBootstrap was deleted (Capacitor initialization)
import { initAuthBootstrap } from "./lib/authBootstrap";
import { installApiAuthInterceptor } from "./lib/api";
import { DEPLOY_TRIGGER_BUILD_TS } from "./deploy-trigger";
import { queryDebug } from "./lib/queryDebug";
import Status from "./pages/Status";
import { CampaignFiltersProvider } from "./context/CampaignFiltersContext";
import { persistQueryClient } from '@tanstack/query-persist-client-core';
import { persister } from './lib/capacitorPersister';
// CAPACITOR REMOVED: SplashScreen and push notifications - mobile app is now billposter-expo

const STALE_CHUNK_RELOAD_KEY = 'reactor:stale-chunk-reload';

function recoverFromStaleChunk(error: unknown) {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const isStaleChunkError = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(message);

  if (!isStaleChunkError) {
    return false;
  }

  const currentBundle = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src
    ?? window.location.origin;

  if (window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) === currentBundle) {
    return false;
  }

  window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, currentBundle);
  window.location.reload();
  return true;
}

// Vite emits this when an open tab references a lazy chunk from an older deployment.
window.addEventListener('vite:preloadError', (event) => {
  if (recoverFromStaleChunk(event.payload)) {
    event.preventDefault();
  }
});


// Global error handlers for debugging white screen issues
window.addEventListener('error', (event) => {
  console.error('[GLOBAL ERROR]', event.error);
  console.error('[ERROR DETAILS]', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    stack: event.error?.stack
  });
});

window.addEventListener('unhandledrejection', (event) => {
  if (recoverFromStaleChunk(event.reason)) {
    event.preventDefault();
    return;
  }

  console.error('[UNHANDLED PROMISE REJECTION]', event.reason);
  console.error('[REJECTION DETAILS]', {
    reason: event.reason,
    promise: event.promise
  });
});

// CAPACITOR REMOVED: Native platform logging - mobile app is now billposter-expo

console.log('[INIT] Starting app initialization...');

function runEnvPreflight() {
  const missing: string[] = [];

  if (!import.meta.env.VITE_SUPABASE_URL) {
    missing.push('VITE_SUPABASE_URL');
  }

  if (!import.meta.env.VITE_SUPABASE_ANON_KEY) {
    missing.push('VITE_SUPABASE_ANON_KEY');
  }

  if (import.meta.env.DEV && !import.meta.env.VITE_API_BASE) {
    missing.push('VITE_API_BASE');
  }

  if (missing.length > 0) {
    console.error(
      `[ENV_PREFLIGHT] Missing required Vite env vars: ${missing.join(', ')}. Login/API calls may fail in local dev.`
    );
  } else {
    console.log('[ENV_PREFLIGHT] Required Vite env vars detected');
  }

  if (import.meta.env.PROD && import.meta.env.VITE_API_BASE && /localhost|127\.0\.0\.1/.test(import.meta.env.VITE_API_BASE)) {
    console.error('[ENV_PREFLIGHT] Production build has localhost VITE_API_BASE — login will fail');
  }
}

runEnvPreflight();

// Initialize Supabase auth bootstrap
initAuthBootstrap();

// Ensure all /api requests carry Supabase JWT automatically
installApiAuthInterceptor();


// Log build commit for deployment verification
try {
  // eslint-disable-next-line no-console
  console.log('[BUILD]', 'Commit:', (typeof __BUILD_COMMIT__ !== 'undefined' ? __BUILD_COMMIT__ : 'unknown'));
} catch { }

// no-op reference so Vercel notices change
if (typeof window !== 'undefined') {
  void DEPLOY_TRIGGER_BUILD_TS;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,      // 5 minutes
      gcTime: 10 * 60 * 1000,        // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: false,          // Don't force refetch on mount - use cache first
      retry: (failureCount, error: any) => {
        // Don't retry when offline or on network errors
        if (error?.status === 0 || error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
          return false;
        }
        return failureCount < 2;
      },
      networkMode: 'offlineFirst',   // Use cache when offline, attempt network when online
    },
    mutations: {
      retry: 1,
      networkMode: 'online',          // Mutations require online connection
    },
  },
});

// Export queryClient for reuse across the app
export { queryClient };

// Refresh store signed URLs on reconnect — pairs with 48 h TTL in storefrontPhotoUrl.ts.
// When the browser comes back online after an offline period, cached signed URLs may be
// near or past their expiry window, so we invalidate all /api/stores queries to force
// a fresh fetch and re-mint of signed URLs.
window.addEventListener('online', () => {
  queryClient.invalidateQueries({ queryKey: ['/api/stores'] });
});

// Persist query cache to IndexedDB for offline viewing
persistQueryClient({
  queryClient,
  persister,
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => {
      try {
        // Safely access queryKey
        if (!query || !Array.isArray(query.queryKey) || query.queryKey.length === 0) {
          return false;
        }

        const key = query.queryKey[0];
        if (typeof key !== 'string') {
          return false;
        }

        // Store Profile detail records contain live signed photo URLs and must
        // never be restored from the 24-hour offline cache.
        if (query.meta?.persist === false) {
          return false;
        }

        const isGroupsViewQuery =
          key.includes('/groups-view') ||
          query.queryKey.some(part => part === 'groups-view' || (typeof part === 'string' && part.includes('/groups-view')));

        if (isGroupsViewQuery) {
          return false;
        }

        // Persist Stage 1 offline data: campaigns, packs, tasks, stores
        // Campaigns - match any query key starting with '/api/campaigns' (including with params)
        const shouldPersist =
          key === '/api/campaigns' ||
          key.startsWith('/api/campaigns') ||
          key === '/api/packs' ||
          key.startsWith('/api/packs') ||
          key === '/api/stores' ||
          key.startsWith('/api/stores') ||
          key === '/api/tasks' ||
          key === '/api/operator-tasks' ||
          key === 'operatorTasks' ||
          key.startsWith('/api/tasks') ||
          key.startsWith('/api/admin/tasks') ||
          key === '/api/posting-instructions' ||
          key.startsWith('/api/locked-cycles/') ||
          // Store visit queries for offline viewing
          key === 'storeVisit' ||
          // Mobile tasks endpoint (used by store detail)
          key.startsWith('/api/mobile/tasks') ||
          // Retailer-specific queries
          ((key === '/api/retailers' || key === 'retailers') && query.queryKey.length >= 3 &&
            (query.queryKey[2] === 'packs' || query.queryKey[2] === 'stores'));

        if (shouldPersist && query.state?.data) {
          console.log('[PersistQueryClient] Persisting query:', key, 'has data:', !!query.state.data);
        }

        return shouldPersist;
      } catch (error) {
        console.error('[PersistQueryClient] shouldDehydrateQuery error:', error);
        return false;
      }
    },
  },
});

// Initialize query debugging in development
// INTENTIONAL STABILITY: disabled temporarily to avoid observer noise/issues
// during auth bootstrap; re-enable after TanStack upgrade.
// if (import.meta.env.DEV) {
//   queryDebug(queryClient);
// }

const isStatusRoute = typeof window !== 'undefined' && (window.location.pathname === '/status' || window.location.pathname === '/__status');

if (isStatusRoute) {
  createRoot(document.getElementById("root")!).render(
    <ErrorBoundary>
      <Status />
    </ErrorBoundary>
  );
} else {
  createRoot(document.getElementById("root")!).render(
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CampaignFiltersProvider>
            <App />
          </CampaignFiltersProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
  // CAPACITOR REMOVED: SplashScreen and push notification code - mobile app is now billposter-expo
}
